import { type FC, type ReactElement } from "react";
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import i18next from "i18next";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import type { ThreadChannel, ThreadEvent, ThreadMessage } from "./types";
import { CHANNEL_HINT, EVENT_TEXT, threadTexts } from "./texts";
import { safeHref } from "./model/safeHref";
import { formatFileSize } from "./model/people";
import { MessageRow } from "./MessageRow";

interface Props {
  messages: ThreadMessage[];
  formatTime: (iso: string) => string;
  onShowOriginal?: (messageId: string) => void;
  /** Имя зрителя для подписи и букв аватара; без него «Зритель». */
  viewerName?: string | null;
}

const CHANNEL_ICON: Record<ThreadChannel, ReactElement> = {
  EMAIL: <EmailOutlinedIcon sx={{ fontSize: 16 }} />,
  SITE: <LanguageOutlinedIcon sx={{ fontSize: 16 }} />,
  CABINET: <DashboardOutlinedIcon sx={{ fontSize: 16 }} />,
  SYSTEM: <SettingsOutlinedIcon sx={{ fontSize: 16 }} />,
};

type ChipEvent = Exclude<ThreadEvent, "FOREIGN_SENDER" | "REOPEN_BLOCKED">;

const EVENT_ICON: Record<ChipEvent, ReactElement> = {
  ASSIGNED: <AssignmentIndOutlinedIcon />,
  UNASSIGNED: <PersonOffOutlinedIcon />,
  RESOLVED: <CheckCircleOutlineIcon />,
  REOPENED: <ReplayIcon />,
  AUTO_CLOSED: <ScheduleOutlinedIcon />,
};

const EVENT_COLOR: Record<ChipEvent, "default" | "success"> = {
  ASSIGNED: "default",
  UNASSIGNED: "default",
  RESOLVED: "success",
  REOPENED: "default",
  AUTO_CLOSED: "default",
};

const eventText = (message: ThreadMessage): string =>
  message.event
    ? EVENT_TEXT[message.event]({
        name: message.eventUserName ?? message.staffName ?? "",
        email: message.fromEmail ?? "",
      })
    : "";

/**
 * Письмо пришло не с адреса зрителя: короткое предупреждение с адресом
 * отправителя (длинный адрес обрезан, целиком в подсказке) и текстом.
 */
const ForeignSender: FC<{
  message: ThreadMessage;
  time: string;
}> = ({ message, time }) => (
  <Alert
    data-author="SYSTEM"
    data-event="FOREIGN_SENDER"
    severity="warning"
    variant="outlined"
    sx={{ "& .MuiAlert-message": { minWidth: 0, flex: 1 } }}
  >
    <AlertTitle sx={{ mb: 0.25 }}>
      {threadTexts.foreignSenderTitle()}
    </AlertTitle>
    <Stack
      direction="row"
      spacing={1}
      alignItems="baseline"
      sx={{ minWidth: 0 }}
    >
      <Tooltip title={message.fromEmail ?? ""}>
        <Typography variant="body2" noWrap sx={{ minWidth: 0, flex: 1 }}>
          {message.fromEmail}
        </Typography>
      </Tooltip>
      <Typography variant="caption" color="text.secondary" flexShrink={0}>
        {time}
      </Typography>
    </Stack>
    {message.body && (
      <Typography
        variant="body2"
        sx={{ mt: 1, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {message.body}
      </Typography>
    )}
  </Alert>
);

/** Системное событие: небольшой чип на линии посередине ленты. */
const SystemLine: FC<{
  message: ThreadMessage;
  formatTime: Props["formatTime"];
}> = ({ message, formatTime }) => {
  const time = formatTime(message.createdAt);
  if (message.event === "FOREIGN_SENDER") {
    return <ForeignSender message={message} time={time} />;
  }
  if (message.event === "REOPEN_BLOCKED") {
    return (
      <Alert data-author="SYSTEM" severity="info" variant="outlined">
        {eventText(message)}
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mt: 0.5 }}
        >
          {time}
        </Typography>
      </Alert>
    );
  }
  const event = message.event as ChipEvent | null;
  return (
    <Divider
      data-author="SYSTEM"
      sx={{
        // Линии по бокам не сжимают подпись события: она берёт сколько
        // нужно, длинная обрезается многоточием у края.
        "& .MuiDivider-wrapper": { flexShrink: 0, maxWidth: "85%" },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ minWidth: 0 }}
      >
        <Chip
          size="small"
          variant="outlined"
          color={event ? EVENT_COLOR[event] : "default"}
          icon={event ? EVENT_ICON[event] : undefined}
          label={eventText(message)}
          sx={{ minWidth: 0 }}
        />
        <Typography variant="caption" color="text.secondary" flexShrink={0}>
          {time}
        </Typography>
      </Stack>
    </Divider>
  );
};

const Attachments: FC<{ message: ThreadMessage }> = ({ message }) => {
  if (!message.attachments.length) return null;
  const locale = i18next.language || "ru";
  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      flexWrap="wrap"
      sx={{ mt: 1.25 }}
    >
      {message.attachments.map((a, index) => {
        const href = safeHref(a.url);
        const size = formatFileSize(a.size, locale);
        const label = size ? `${a.name}, ${size}` : a.name;
        if (!href) {
          // Ссылка не http(s): имя файла без ссылки.
          return (
            <Chip
              key={`${index}-${a.name}`}
              size="small"
              variant="outlined"
              icon={<AttachFileIcon />}
              label={label}
            />
          );
        }
        if (a.mimeType.startsWith("image/")) {
          return (
            <Tooltip key={`${index}-${href}`} title={label}>
              <Link
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "block", lineHeight: 0 }}
              >
                <Box
                  component="img"
                  src={href}
                  alt={a.name}
                  sx={{
                    maxWidth: 180,
                    maxHeight: 128,
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    display: "block",
                  }}
                />
              </Link>
            </Tooltip>
          );
        }
        return (
          <Chip
            key={`${index}-${href}`}
            size="small"
            variant="outlined"
            clickable
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            icon={<InsertDriveFileOutlinedIcon />}
            label={label}
          />
        );
      })}
    </Stack>
  );
};

const Message: FC<{
  message: ThreadMessage;
  formatTime: Props["formatTime"];
  onShowOriginal?: Props["onShowOriginal"];
  viewerName?: string | null;
}> = ({ message, formatTime, onShowOriginal, viewerName }) => {
  const fromViewer = message.author === "VIEWER";
  const name = fromViewer
    ? (viewerName ?? threadTexts.viewer())
    : (message.staffName ?? threadTexts.team());
  const channelHint = CHANNEL_HINT[message.channel]();
  return (
    <MessageRow
      tone={fromViewer ? "viewer" : message.internal ? "note" : "team"}
      name={name}
      avatarName={fromViewer ? viewerName : message.staffName}
      time={formatTime(message.createdAt)}
      dataAttributes={{
        "data-author": message.author,
        "data-internal": String(message.internal),
        "data-channel": message.channel,
      }}
      meta={
        <>
          <Tooltip title={channelHint}>
            <Box
              component="span"
              role="img"
              aria-label={channelHint}
              sx={{ display: "inline-flex", color: "text.secondary" }}
            >
              {CHANNEL_ICON[message.channel]}
            </Box>
          </Tooltip>
          {message.viaAssistant && (
            <Typography variant="caption" color="text.secondary">
              {threadTexts.viaAssistant()}
            </Typography>
          )}
        </>
      }
      actions={
        message.hasOriginal && onShowOriginal ? (
          <Button
            size="small"
            color="inherit"
            startIcon={<MarkEmailReadOutlinedIcon />}
            onClick={() => onShowOriginal(message.id)}
            sx={{ py: 0, color: "text.secondary" }}
          >
            {threadTexts.showOriginal()}
          </Button>
        ) : undefined
      }
    >
      <Typography
        variant="body1"
        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {message.body}
      </Typography>
      <Attachments message={message} />
      {message.skippedAttachments > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mt: 0.75 }}
        >
          {threadTexts.attachmentsSkipped(message.skippedAttachments)}
        </Typography>
      )}
    </MessageRow>
  );
};

/**
 * Лента переписки на своей поверхности: зритель слева, команда справа,
 * события чипами на линии посередине (спека 3.1).
 */
export const ThreadFeed: FC<Props> = ({
  messages,
  formatTime,
  onShowOriginal,
  viewerName,
}) => (
  <Card>
    <CardHeader
      avatar={
        <Avatar sx={{ bgcolor: "action.selected", color: "text.primary" }}>
          <ForumOutlinedIcon />
        </Avatar>
      }
      title={threadTexts.threadTitle()}
      slotProps={{ title: { variant: "subtitle1", fontWeight: 600 } }}
      action={
        messages.length > 0 ? (
          <Chip size="small" label={messages.length} sx={{ mt: 1, mr: 1 }} />
        ) : undefined
      }
      sx={{ px: 2, pt: 2, pb: 0 }}
    />
    <CardContent sx={{ px: 2, pb: 2, pt: 2.5 }}>
      {!messages.length ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 3 }}>
          <ReplyOutlinedIcon color="disabled" />
          <Typography variant="body2" color="text.secondary">
            {threadTexts.emptyThread()}
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          {messages.map((m) =>
            m.author === "SYSTEM" ? (
              <SystemLine key={m.id} message={m} formatTime={formatTime} />
            ) : (
              <Message
                key={m.id}
                message={m}
                formatTime={formatTime}
                onShowOriginal={onShowOriginal}
                viewerName={viewerName}
              />
            )
          )}
        </Stack>
      )}
    </CardContent>
  </Card>
);
